import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog.tsx';
import { toast } from 'sonner';
import type { CognitoUser } from '@metron/shared';
import { createLogger } from '@metron/shared';

const logger = createLogger('ResendWelcomeEmailDialog');

interface ResendWelcomeEmailDialogProps {
  open: boolean;
  user: CognitoUser | null;
  onOpenChange: (open: boolean) => void;
  onResendWelcomeEmail: (userId: string) => Promise<void>;
}

export function ResendWelcomeEmailDialog({
  open,
  user,
  onOpenChange,
  onResendWelcomeEmail,
}: ResendWelcomeEmailDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleResend = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      await onResendWelcomeEmail(user.username);
      toast.success('Welcome email resent successfully');
      onOpenChange(false);
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to resend welcome email';
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to resend welcome email');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resend Welcome Email</AlertDialogTitle>
          <AlertDialogDescription>
            Resend the welcome email with temporary password to {user.name} ({user.email})?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className='py-2'>
          <p className='text-sm text-muted-foreground'>
            This will send a new email to the user with their temporary password. The user will be
            required to change this password on their first login.
          </p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleResend} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Resend Email'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
